/**
 * CartRetentionPoliciesPage — System · Cart Retention Policies.
 *
 * Per-(origin × resolution) override of how long a closed cart stays
 * restorable before the hard-delete sweep removes it. Composite natural key
 * `(originRole, resolutionCode)`. Empty (origin × resolution) cells inherit
 * the platform default of 2555 days (7 years). The Adverse-Action review
 * queue and the per-cart Restore button both honor these windows.
 *
 * Contract (verified against the backend 2026-06-16 — entity, upsert DTO,
 * service, and read route all share identical camelCase field names; NO drift
 * found):
 *   - Read:   GET    /v3/admin/cart-retention-policies
 *             → AdminCartRetentionFunction.List returns
 *               { success, data: CartRetentionPolicy[] }. Returns ALL rows
 *               (no WHERE IsActive filter) so inactive overrides stay visible
 *               for audit. Typed via client.retail.admin.cartRetentionPolicies
 *               .policies.list() : Promise<ApiResult<CartRetentionPolicy[]>>
 *               (envelope shape matches the wire — no cast needed).
 *   - Write:  POST   /v3/admin/cart-retention-policies
 *             → AdminCartRetentionFunction.Upsert. EF createOrUpdate keyed on
 *               (OriginRole, ResolutionCode). Persists EVERY field on both the
 *               create and update branches (retentionDays/isActive/notes) — no
 *               silent drop.
 *   - Delete: DELETE /v3/admin/cart-retention-policies/{originRole}/{resolutionCode}
 *             → SOFT-delete (sets IsActive=false, keeps the audit row). The
 *               List route surfaces inactive rows, and isActive is independently
 *               editable via upsert, so "Reset to default" deactivates the
 *               override; the platform 2555-day default then applies. Typed via
 *               client.retail.admin.cartRetentionPolicies.policies.remove().
 *
 * Migrated 2026-06-16 from ROP-teams Admin
 * (apps/ROP-teams/src/modules/Admin/editors/cartRetentionPoliciesEditor.tsx —
 * the operational app writes no config).
 * Auth: ADMIN_AUTH_TOKEN_SENTINEL (MSAL bearer; no secret in the bundle).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ADMIN_AUTH_TOKEN_SENTINEL, ApiError } from '@retail-ops/api-client';
import type {
  CartOriginRole,
  CartResolutionCode,
  CartRetentionPolicy,
  CartRetentionPolicyUpsertInput,
} from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { SelectField, NumberField, BoolField, TextArea } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

const DEFAULT_DAYS = 2555; // 7 years
const MIN_DAYS = 30;
const MAX_DAYS = 3650;

const ALL_ORIGINS: ReadonlyArray<CartOriginRole> = [
  'fsr-self',
  'lobby-queue',
  'lobby-entry-internal',
  'lobby-entry-external',
  'kiosk',
  'rcp-portal',
];

// 'Open' is an in-flight cart, not a closed one — never gets a retention window.
const ALL_CODES: ReadonlyArray<CartResolutionCode> = [
  'Processed',
  'Withdrawn',
  'Adverse-Action',
  'Cancelled',
  'System-Error',
];

const ORIGIN_LABEL: Record<CartOriginRole, string> = {
  'fsr-self': 'FSR self-start (branch)',
  'lobby-queue': 'Lobby queue',
  'lobby-entry-internal': 'Lobby entry — internal',
  'lobby-entry-external': 'Lobby entry — external',
  kiosk: 'Kiosk',
  'rcp-portal': 'Customer portal (RCP)',
};

interface PolicyRow {
  originRole: CartOriginRole;
  resolutionCode: CartResolutionCode;
  retentionDays: number;
  isActive: boolean;
  notes: string | null;
}

interface EditDraft extends PolicyRow {
  isNew: boolean;
  /** Existing rows lock the (origin × resolution) composite key. */
  lockedKey: boolean;
}

function emptyDraft(): EditDraft {
  return {
    originRole: 'fsr-self',
    resolutionCode: 'Cancelled',
    retentionDays: DEFAULT_DAYS,
    isActive: true,
    notes: '',
    isNew: true,
    lockedKey: false,
  };
}

function rowToDraft(r: PolicyRow): EditDraft {
  return { ...r, isNew: false, lockedKey: true };
}

function draftToUpsertBody(d: EditDraft): CartRetentionPolicyUpsertInput {
  return {
    originRole: d.originRole,
    resolutionCode: d.resolutionCode,
    retentionDays: d.retentionDays,
    isActive: d.isActive,
    notes: d.notes?.trim() ? d.notes.trim() : undefined,
  };
}

function fmtDays(n: number): string {
  const years = n / 365;
  if (Number.isInteger(years)) return `${n} days (${years}y)`;
  return `${n} days`;
}

const ORIGIN_OPTIONS = ALL_ORIGINS.map((o) => ({ value: o, label: ORIGIN_LABEL[o] }));
const CODE_OPTIONS = ALL_CODES.map((c) => ({ value: c, label: c }));

export function CartRetentionPoliciesPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<PolicyRow> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [resetTarget, setResetTarget] = useState<PolicyRow | null>(null);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setRows(null);
      setLoadError(null);
      try {
        const resp = await client.retail.admin.cartRetentionPolicies.policies.list();
        if (cancelled) return;
        if (resp.success && Array.isArray(resp.data)) {
          const mapped: PolicyRow[] = resp.data.map((p: CartRetentionPolicy) => ({
            originRole: p.originRole,
            resolutionCode: p.resolutionCode,
            retentionDays: p.retentionDays,
            isActive: p.isActive,
            notes: p.notes ?? null,
          }));
          setRows(mapped);
        } else {
          setLoadError(resp.error ?? 'Failed to load cart retention policies.');
        }
      } catch (e) {
        if (cancelled) return;
        setLoadError(
          ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const visible = useMemo(() => {
    const sorted = (rows ?? [])
      .slice()
      .sort(
        (a, b) =>
          a.originRole.localeCompare(b.originRole) ||
          a.resolutionCode.localeCompare(b.resolutionCode),
      );
    const needle = filter.trim().toLowerCase();
    if (!needle) return sorted;
    return sorted.filter(
      (r) =>
        r.originRole.toLowerCase().includes(needle) ||
        ORIGIN_LABEL[r.originRole].toLowerCase().includes(needle) ||
        r.resolutionCode.toLowerCase().includes(needle),
    );
  }, [rows, filter]);

  const handleSave = useCallback(async () => {
    if (!draft) return;
    if (
      !Number.isFinite(draft.retentionDays) ||
      draft.retentionDays < MIN_DAYS ||
      draft.retentionDays > MAX_DAYS
    ) {
      setSaveError(`Retention must be between ${MIN_DAYS} and ${MAX_DAYS} days.`);
      return;
    }
    // Guard against creating a duplicate composite key.
    if (
      draft.isNew &&
      (rows ?? []).some(
        (r) => r.originRole === draft.originRole && r.resolutionCode === draft.resolutionCode,
      )
    ) {
      setSaveError(
        `An override for ${ORIGIN_LABEL[draft.originRole]} / ${draft.resolutionCode} already exists. Edit it instead.`,
      );
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const resp = await client.retail.admin.cartRetentionPolicies.policies.upsert(
        ADMIN_AUTH_TOKEN_SENTINEL,
        draftToUpsertBody(draft),
      );
      if (!resp.success) {
        setSaveError(resp.error ?? 'admin upsert returned success=false');
        return;
      }
      setDraft(null);
      setToast({
        tone: 'info',
        text: draft.isNew
          ? `Created retention override for ${ORIGIN_LABEL[draft.originRole]} / ${draft.resolutionCode}.`
          : `Updated retention for ${ORIGIN_LABEL[draft.originRole]} / ${draft.resolutionCode}.`,
      });
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [draft, rows, client, refresh]);

  const handleReset = useCallback(async () => {
    if (!resetTarget) return;
    setSaving(true);
    setSaveError(null);
    try {
      const resp = await client.retail.admin.cartRetentionPolicies.policies.remove(
        ADMIN_AUTH_TOKEN_SENTINEL,
        resetTarget.originRole,
        resetTarget.resolutionCode,
      );
      if (!resp.success) {
        setSaveError(resp.error ?? 'delete returned success=false');
        return;
      }
      setToast({
        tone: 'warn',
        text: `Override for ${ORIGIN_LABEL[resetTarget.originRole]} / ${resetTarget.resolutionCode} reset — the ${DEFAULT_DAYS}-day default now applies.`,
      });
      setResetTarget(null);
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [resetTarget, client, refresh]);

  const columns: ReadonlyArray<DataTableColumn<PolicyRow>> = useMemo(
    () => [
      {
        header: 'Origin',
        cell: (r) => (
          <span className="cell-strong">
            {ORIGIN_LABEL[r.originRole]} <code className="cell-mono">{r.originRole}</code>
          </span>
        ),
      },
      {
        header: 'Resolution',
        cell: (r) => <code className="cell-mono--bold">{r.resolutionCode}</code>,
      },
      {
        header: 'Retention',
        cell: (r) => <span className="num">{fmtDays(r.retentionDays)}</span>,
      },
      {
        header: 'Notes',
        cell: (r) => <span className="cell-desc">{r.notes || '—'}</span>,
      },
      {
        header: 'Status',
        cell: (r) =>
          r.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">inactive (default)</Pill>,
      },
      {
        header: '',
        cell: (r) => (
          <div className="card-actions-row">
            <Button onClick={() => { setDraft(rowToDraft(r)); setSaveError(null); }}>Edit</Button>
            {r.isActive && <Button onClick={() => setResetTarget(r)}>Reset</Button>}
          </div>
        ),
      },
    ],
    [],
  );

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load cart retention policies: {loadError}{' '}
        <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!rows) return <Spinner label="Loading cart retention policies…" />;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Banner tone="info">
        Every closed cart stays restorable for a retention window before the hard-delete sweep
        removes it. The platform default is <strong>{DEFAULT_DAYS} days (7 years)</strong>; add an
        override per <strong>(origin × resolution)</strong> only when a regulatory or operational
        requirement differs. The Adverse-Action review queue and the per-cart Restore button both
        honor these windows.
      </Banner>

      <Card
        title={`Cart Retention Policies — ${visible.length} of ${rows.length}`}
        actions={
          <div className="card-actions-row">
            <div className="users-filter">
              <SelectField
                label=""
                value={filter}
                onChange={setFilter}
                options={[
                  { value: '', label: 'All origins & resolutions' },
                  ...ALL_ORIGINS.map((o) => ({ value: o, label: ORIGIN_LABEL[o] })),
                  ...ALL_CODES.map((c) => ({ value: c, label: c })),
                ]}
              />
            </div>
            <Button variant="primary" onClick={() => { setDraft(emptyDraft()); setSaveError(null); }}>
              + Add Override
            </Button>
          </div>
        }
      >
        <p className="cell-desc">
          Only overrides are listed. Any (origin × resolution) without a row inherits the{' '}
          {DEFAULT_DAYS}-day default. Resetting an override deactivates it and restores the default.
        </p>
        <DataTable<PolicyRow>
          columns={columns}
          rows={visible}
          rowKey={(r) => `${r.originRole}|${r.resolutionCode}`}
          emptyState={
            <p className="empty">
              No overrides defined — every cart uses the {DEFAULT_DAYS}-day default.
            </p>
          }
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={
          draft?.isNew
            ? 'Add retention override'
            : `Edit ${draft?.originRole ?? ''} / ${draft?.resolutionCode ?? ''}`
        }
        onClose={() => {
          if (saving) return;
          setDraft(null);
          setSaveError(null);
        }}
        width={620}
        actions={
          draft && (
            <>
              <Button onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Saving…' : draft.isNew ? 'Create' : 'Save changes'}
              </Button>
            </>
          )
        }
      >
        {draft && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}

            <div className="form-row">
              <SelectField
                label="Origin"
                value={draft.originRole}
                onChange={(v) => setDraft({ ...draft, originRole: v as CartOriginRole })}
                options={
                  draft.lockedKey
                    ? [{ value: draft.originRole, label: ORIGIN_LABEL[draft.originRole] }]
                    : ORIGIN_OPTIONS
                }
                disabled={draft.lockedKey}
                hint={
                  draft.lockedKey
                    ? 'Composite key is read-only on existing rows. Reset and re-create to re-key.'
                    : 'Where the cart originated.'
                }
              />
              <SelectField
                label="Resolution code"
                value={draft.resolutionCode}
                onChange={(v) => setDraft({ ...draft, resolutionCode: v as CartResolutionCode })}
                options={
                  draft.lockedKey
                    ? [{ value: draft.resolutionCode, label: draft.resolutionCode }]
                    : CODE_OPTIONS
                }
                disabled={draft.lockedKey}
                hint={
                  draft.lockedKey
                    ? undefined
                    : "How the cart closed. 'Open' is excluded — it's an in-flight cart."
                }
              />
            </div>

            <NumberField
              label="Retention (days)"
              value={draft.retentionDays}
              onChange={(v) => setDraft({ ...draft, retentionDays: v ?? DEFAULT_DAYS })}
              min={MIN_DAYS}
              max={MAX_DAYS}
              hint={`Between ${MIN_DAYS} and ${MAX_DAYS}. Default ${DEFAULT_DAYS} (7 years). Stored as integer days.`}
            />

            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
              hint="When inactive, this override is ignored and the platform default applies. Row stays for audit."
            />

            <TextArea
              label="Notes"
              value={draft.notes ?? ''}
              onChange={(v) => setDraft({ ...draft, notes: v || null })}
              rows={3}
              placeholder="Why this override exists (regulator letter, audit finding, etc.)."
            />
          </div>
        )}
      </ModalShell>

      <ModalShell
        open={resetTarget !== null}
        title="Reset to default?"
        onClose={() => {
          if (saving) return;
          setResetTarget(null);
          setSaveError(null);
        }}
        width={520}
        actions={
          resetTarget && (
            <>
              <Button onClick={() => setResetTarget(null)} disabled={saving}>
                Keep override
              </Button>
              <Button variant="primary" onClick={() => void handleReset()} disabled={saving}>
                {saving ? 'Working…' : 'Reset'}
              </Button>
            </>
          )
        }
      >
        {resetTarget && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}
            <p className="modal-prose">
              We&apos;ll deactivate the override for{' '}
              <code className="cell-mono">
                {resetTarget.originRole} / {resetTarget.resolutionCode}
              </code>
              . The platform default of {DEFAULT_DAYS} days (7 years) will apply on the next cart
              resolution. The row is kept inactive for audit — flip it back on anytime by editing it.
            </p>
          </div>
        )}
      </ModalShell>
    </>
  );
}
